import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import {
  ErrorMessage,
  GlobalStyle,
  Logo,
} from "./BetaComponents";
import { ArmDown1HeroMan, ArmDown2HeroMan, ArmUp1HeroMan, ArmUp2HeroMan, BlinkingtHeroMan, DefaultHeroMan } from "./LandingPageIcons/HeroMan";
import {
  DryCleaningIcon,
  HardwareIcon,
  HealthcareProviderIcon,
  MechanicIcon,
  PlusIcon,
  SpeechBubble,
  TodoListCheck,
  Instagram,
  TikTok
} from "./LandingPageIcons/LandingPageIcons";
import { MapMan } from "./LandingPageIcons/MapMan";
import { PhoneMessageIcon } from "./LandingPageIcons/PhoneTextingIcon";
import { SofaMan } from "./LandingPageIcons/SofaMan";

const MOBILE_WIDTH = "1024px";
const SIDEBAR_WIDTH = "480px";

const HeroMan = styled.div`
  max-width: 850px;
  min-width: 500px;
  width: 100%;
  height: auto;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 70px;

  > svg {
    width: 100%;
    height: auto;
  }
`;

type Pose = "default" | "blink" | "armUp1" | "armUp2" | "armDown1" | "armDown2";

const PoseView: Record<Pose, React.ReactNode> = {
  default: <DefaultHeroMan />,
  blink: <BlinkingtHeroMan />,
  armUp1: <ArmUp1HeroMan />,
  armUp2: <ArmUp2HeroMan />,
  armDown1: <ArmDown1HeroMan />,
  armDown2: <ArmDown2HeroMan />,
};

const AnimatedMascot: React.FC = () => {
  const [pose, setPose] = useState<Pose>("default");
  // Blink every ~3–5 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      setPose((prev) => {
        // don't start a blink if we're in the middle of a wave
        if (prev !== "default") return prev;
        setPose("blink");
        setTimeout(() => {
          setPose((curr) => (curr === "blink" ? "default" : curr));
        }, 120); // blink length
        return prev; // we already kicked off blink via setPose above
      });
    }, 3000 + Math.random() * 2000); // 3–5s
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Wave every 8 seconds
  useEffect(() => {
    const waveInterval = setInterval(() => {
      // use functional update so we always see the latest pose
      setPose((prev) => {
        if (prev !== "default") return prev;
        const waveSteps: Pose[] = [
          "default",
          "armUp1",
          "armUp2",
          "armUp2",
          "armUp1",
          "armDown1",
          "armDown2",
          "armDown2",
          "armDown1",
          "armUp1",
          "armUp2",
          "armUp1",
          "default"
        ];
        waveSteps.forEach((step, index) => {
          setTimeout(() => {
            setPose(step);
          }, index * 150);
        });
        return prev;
      });
    }, 150*15 + 8000);
    return () => clearInterval(waveInterval);
  }, []);

  return (
    <HeroMan>
      {PoseView[pose]}
    </HeroMan>
  );
}

const LandingPageSidebar = styled.aside`
  background: #f63700;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  > :last-child {
    margin-top: auto;
  }

  @media (min-width: ${MOBILE_WIDTH}) {
    position: fixed;
    left: 0;
    top: 0;
    width: ${SIDEBAR_WIDTH};
    height: 100dvh;
    overflow: hidden;
  }

  @media (max-width: 1023.98px) {
    position: sticky;
    top: 0;
    width: 100%;
    min-height: 100dvh;
    z-index: 1;
    padding-bottom: 0;
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 100%;
  gap: 40px;

  @media (max-width: 1023.98px) {
    margin-top: 70px;
  }
  
`;

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  color: #FFCBA4;
  text-align: center;
  font-family: "Plus Jakarta Sans";
  font-size: 30px;
  font-style: normal;
  font-weight: 500;
  line-height: 115%;

  @media (max-width: ${MOBILE_WIDTH}) {
    font-size: 25px;
  }
`;

const SubHero = styled.div`
  color: #FFCBA4;
  text-align: center;
  font-family: "Plus Jakarta Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 115%;
  letter-spacing: 0.7px;
  text-transform: uppercase;
`;

const CTAButton = styled.button`
  display: flex;
  width: 268px;
  height: 65px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: #FD8C0B;
  border: none;
  cursor: pointer;
  margin-bottom: 40px;
`;

const CTAText = styled.div`
  text-align: center;
  color: #FFF;
  font-family: "Plus Jakarta Sans";
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`;

type SideBarProps = {
  onSignUp: () => void;
};

const SideBar = ({ onSignUp }: SideBarProps) => {
    return (
      <LandingPageSidebar>
        <SidebarContent>
          <Logo largeFont/>
          <Hero>AI that makes phone calls for you</Hero>
          <SubHero>Doctors • Auto • Salons • Anything</SubHero>
        </SidebarContent>
        <CTAButton onClick={onSignUp}><CTAText>Get Started</CTAText></CTAButton>
      </LandingPageSidebar>
    );
}

const HeroSectionWrapper = styled.section`
  background: #FFD395;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  justify-content: center;
  overflow: clip;

  @media (min-width: 1024px) {
    min-height: 100dvh;
  }

  @media (max-width: 1023.98px) {
    position: sticky;
    top: 0;
    min-height: 100dvh;
    z-index: 2;
    flex-direction: column-reverse;
  }

`;

const TICKER_BANNER_HEIGHT = 45;

const TickerBanner = styled.div`
  height: ${TICKER_BANNER_HEIGHT}px;
  background: #FD8C0B;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 9999;
`;


const scroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    /* distance is dynamic: width of one copy of the items */
    transform: translateX(calc(-1 * var(--marqueeDistance, 0px)));
  }
`;

const TickerInner = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  width: max-content;
  animation: ${scroll} var(--marqueeDuration, 30s) linear infinite;
`;

const TickerText = styled.span`
  display: inline-block;
  color: #FFF0EB;
  text-align: center;
  font-family: "Plus Jakarta Sans";
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 135%;
  letter-spacing: 0.39px;
  text-transform: uppercase;
`;

const TickerEllipse = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none">
    <circle cx="3" cy="3" r="3" fill="#FFF0EB"/>
  </svg>
)

const TickerDot = styled(TickerEllipse)`
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  margin: 0 1rem;
  display: block;
`;

const TickerItem = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-left: 20px;
`;

const TickerSection: React.FC = () => {
  const [distance, setDistance] = useState(0);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const tickerItems = [
    "Find auto shops near me that can change my oil this weekend",
    "Call  Italian restaurants  about private party options",
    "Call dentists near me and ask if they accept my insurance",
  ];
  const repeated = [...tickerItems, ...tickerItems];

  useEffect(() => {
    if (trackRef.current) {
      const fullWidth = trackRef.current.scrollWidth;
      // we rendered items twice, so one copy is half the width
      setDistance(fullWidth / 2);
    }
  }, [tickerItems.length]);

  return (
    <TickerBanner>
      <TickerInner
        ref={trackRef}
        style={
          {
            "--marqueeDistance": `${distance}px`,
            "--marqueeDuration": "30s", // tweak speed here
          } as React.CSSProperties
        }>
        {repeated.map((text, i) => (
          <TickerItem key={`a-${i}`}>
            <TickerText>{`"${text}"`}</TickerText>
            <TickerDot/>
          </TickerItem>
        ))}
      </TickerInner>
    </TickerBanner>
  );
}


const HeroSection: React.FC = () => {
  return (
    <HeroSectionWrapper>
      <AnimatedMascot/>
    </HeroSectionWrapper>
  );
}


const ProductInfoSubTitle= styled.span`
  color: #332115;
  text-align: center;
  font-family: "Plus Jakarta Sans";
  font-size: 48px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -2.4px;

  @media (max-width: ${MOBILE_WIDTH}) {
    font-size: 32px;
  }
`;


const StepsGridContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
  margin-bottom: 80px;

  @media (max-width: 1275px) {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
  }
`;

const StepItem = styled.div`
  display: flex;
  height: 400px;
  width: 320px;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const StepText = styled.div`
  width: 100%;
  padding: 15px 24px;
  color: #332115;
  font-family: "Plus Jakarta Sans";
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.96px;
`;

const StepIcon = styled.div`
  width: 310px;
  height: 315px;
  border-radius: 24px;
  background: rgba(109, 179, 172, 0.42);
  padding: ${(props) => props.todoList ? "26px 40px 0px 40px;" : "0"};
  overflow: hidden;
  display: flex;
  justify-content: center; 
  align-items: center;
  position: relative;

  > svg {
    height: 315px;
    width: auto;
    flex: 0 0 auto; 
  }
`;

const SpeechBubbleWrapper = styled.div`
  width: 130px;
  height: 82px;
  disply: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  background: none;
  position: absolute;
  top: 80px;
  right: 12px;
  pointer-events: none;

  > svg {
    width: 100%;
    display: block;
  }

`;

const SpeechBubbleText = styled.div`
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  width: 70%;
  text-align: left;
  line-height: 1.3;
  color: #332115;
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 124%;
`;

const TodoList = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 10px 10px 0px 0px;
  background: #FAFAFA;
  padding: 26px 26px 0px 26px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const TodoListTitle = styled.div`
  color: #205400;
  font-family: Avenir;
  font-size: 30px;
  font-style: normal;
  font-weight: 900;
  line-height: 120%;
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 20px;
`;

const TodoListItemWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 10px;

  > svg {
    width: 22px;
    height: 21px;
  }

  > div {
    color: #000;
    font-family: Avenir;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%;
    width: 140px;
  }
`;


type ProductStep = {
  text: string;
  icon: React.ReactNode;
  name: string;
};

const HowItWorksSection = () => {
  const personaNameEl = document.getElementById('persona-name');
  let personaName = 'Pip';
  if (personaNameEl) {
    personaName = personaNameEl.textContent?.trim() || 'Pip';
  }

  const steps: ProductStep[] = [
    {
      text: `Text your assistant ${personaName} who you need to call`,
      icon: <PhoneMessageIcon/>,
      name: "text"
    },
    {
      text: "call.chat does the rest",
      icon: <MapMan/>,
      name: "wait"
    },
    {
      text: "Get your results",
      icon: <SofaMan/>,
      name: "results"
    },
    {
      text: "Cross everything off your to-do list",
      icon: <TodoList/>,
      name: "todoList"
    }
  ];

  const todoListItems: string[] = [
    "Find dermatologist",
    "Find mechanic to replace headlight",
    "Update address with gas company",
    "Find restaurant for holiday catering"
  ];


  return (
    <>
      <ProductInfoSubTitle>How it works</ProductInfoSubTitle>
      <StepsGridContainer>
        <StepsGrid>
          {steps.map((step, i) => {
            if (step.name === "results") {
              return (
                <StepItem key={`step-${step.name}`}>
                  <StepIcon>
                    {step.icon}
                    <SpeechBubbleWrapper>
                      <SpeechBubble/>
                      <SpeechBubbleText>I called 3 doctors in your neighborhood...</SpeechBubbleText>
                    </SpeechBubbleWrapper>
                  </StepIcon>
                  <StepText>{`${i+1}. ${step.text}`}</StepText>
                </StepItem>
              );
            } else if (step.name === "todoList") {
              return (
                <StepItem key={`step-${step.name}`}>
                  <StepIcon todoList>
                    <TodoList>
                      <TodoListTitle>To Do List</TodoListTitle>
                      {todoListItems.map((itemText, i) => (
                        <TodoListItemWrapper key={`todo-item-${i}`}>
                          <TodoListCheck/><div>{itemText}</div>
                        </TodoListItemWrapper>
                      ))}
                    </TodoList>
                  </StepIcon>
                  <StepText>{`${i+1}. ${step.text}`}</StepText>
                </StepItem> 
              );                
            }
            return (
              <StepItem key={`step-${step.name}`}>
                <StepIcon>
                  {step.icon}
                </StepIcon>
                <StepText>{`${i+1}. ${step.text}`}</StepText>
              </StepItem> 
            );           
          })}
        </StepsGrid>
      </StepsGridContainer>
    </>
  );
}



const FAQWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 20px;
  padding-bottom: 80px;
  width: 100%;
`;

const FAQItemContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 10px 0px;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #000;
  cursor: pointer;
`;

const FAQItemText = styled.div`
  color: #1E1E1E;
  font-family: "Plus Jakarta Sans";
  font-size: 19px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px;

  @media (max-width: ${MOBILE_WIDTH}) {
    font-size: 16px;
  }
`;

const ExpandFAQItemButton = styled(PlusIcon)`
  width: 16px;
  height: 16px;
  transform: ${( props ) => (props.$isOpen ? "rotate(45deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
`;

const FAQAnswer = styled(motion.div)`
  overflow: hidden;
  padding: 5px;
  color: #1E1E1E;
  font-family: "Plus Jakarta Sans";
  font-size: 16px;
  font-style: normal;
  font-weight: 400; 
  line-height: 30px;

  > span > a {
    color: #FD8C0B;
  }
`;

type FAQItem = {
  question: string;
  answer: React.ReactNode;
};


const FAQSection = () => {
  const [openFAQIdx, setOpenFAQIdx] = useState<number | null>(null);

  const personaNameEl = document.getElementById('persona-name');
  let personaName = 'Pip';
  if (personaNameEl) {
    personaName = personaNameEl.textContent?.trim() || 'Pip';
  }

  const faqItems: FAQItem[] = [
    {
      question: "What is call.chat?",
      answer: <span>{`call.chat is your new personal A.I. phone-calling assistant. Starting with a simple text message, your assistant ${personaName} can search for businesses or companies based on your request, reach out via phone to them, and have real conversations to gather or confirm information for you - saving you time and mental energy. Sign up now for early access!`}</span>
    },
    {
      question: "How does billing work?",
      answer: <span>call.chat is free for the first 30 days! After that you will automatically be billed $10 per month. But don't worry - we'll remind you when your free trial is about to end, and you can cancel at any time.</span>
    },
    {
      question: "Is my data safe?",
      answer: (
        <span>
          Your data is protected with encryption in transit and at rest, and only used to complete the tasks you request. We give you control - you can delete your messages or request account removal at any time. We process requests using the OpenAI API, which does not use your data to train AI models. Click 
          <a href="https://platform.openai.com/docs/guides/your-data" target="_blank" rel="noopener noreferrer"> here </a> 
          for more information about OpenAI's data privacy.
        </span>
      )
    },
    {
      question: "What if I need more calls?",
      answer: (
        <span>
          We're glad to hear you're loving call.chat! Your subscription comes with up to 200 calls per month, but if you would like more please email us at 
          <a href="mailto:support@call.chat"> support@call.chat</a>
          .
        </span>
      )
    },
  ];

  const handleToggleFAQItem = (idx: number) => {
    setOpenFAQIdx((prev) => (prev === idx ? null : idx)); // click again to close
  };

  return (
    <>
      <ProductInfoSubTitle>FAQ</ProductInfoSubTitle>
      <FAQWrapper>
        {faqItems.map((item, i) => (
          <React.Fragment key={`faq-${i}`}>
            <FAQItemContainer onClick={() => handleToggleFAQItem(i)}>
              <FAQItemText>{item.question}</FAQItemText><ExpandFAQItemButton $isOpen={openFAQIdx === i}/>
            </FAQItemContainer>
            <AnimatePresence initial={false}>
              {openFAQIdx === i && <FAQAnswer
                key={`answer-${i}`}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.1, ease: "easeOut" }}
              >
                {item.answer}
              </FAQAnswer>}
            </AnimatePresence>
          </React.Fragment>
        ))}
      </FAQWrapper>
    </>
  );
}


const UseCasesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  gap: 15px;
  width: 100%;
  margin-bottom: 80px;
`;

const UseCaseWrapper = styled.div`
  display: flex;
  padding: 20px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px;
  width: 155px;
  justify-content: flex-start;
`;

const UseCaseText = styled.div`
  overflow: hidden;
  color: #000;
  text-align: center;
  text-overflow: ellipsis;
  font-family: "Plus Jakarta Sans";
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 135%;
  letter-spacing: -0.3px;
  width: 100%;
`;


type UseCase = {
  text: string;
  icon: React.ReactNode;
};


const UseCasesSection = () => {
  const useCases: UseCase[] = [
    {
      text: "Check local hardware stores for a cordless drill under $100.",
      icon: <HardwareIcon/>
    },
    {
      text: "Can you call PCPs near me and ask if they accept my insurance?",
      icon: <HealthcareProviderIcon/>
    },
    {
      text: "Call my dry cleaner to check if my clothes are ready",
      icon: <DryCleaningIcon/>
    },
    {
      text: "Find auto shops near me that have oil change openings this weekend",
      icon: <MechanicIcon/>
    },
  ];

  return (
    <>
      <ProductInfoSubTitle>Try some of these</ProductInfoSubTitle>
      <UseCasesRow>
        {useCases.map((useCase, i) => (
          <UseCaseWrapper key={`useCase-${i}`}>
            {useCase.icon}
            <UseCaseText>
              {`"${useCase.text}"`}
            </UseCaseText>
          </UseCaseWrapper>
        ))}
      </UseCasesRow>
    </>
  );
}



const SignUpSectionWrapper = styled.section`
  display: flex;
  padding: 80px 20px 40px 20px;
  flex-direction: column;
  align-items: center;
  gap: 44px;
  background: #AABAA2;
  justify-content: center;
  position: relative;

  @media (max-width: 1023.98px) {
    min-height: 100dvh;
    z-index: 4;
  }

  > :last-child {
    margin-top: auto;
  }
`;

const SignUpSectionTitle = styled.div`
  color: #205400;
  text-align: center;
  font-family: "Gasoek One";
  font-size: 80px;
  font-style: normal;
  font-weight: 400;
  line-height: 90%;
  letter-spacing: -0.8px;

  @media (max-width: 1023.98px) {
    font-size: 56px;
    letter-spacing: -0.56px;
  }
`;

const SignUpContent = styled.div`
  display: flex;
  width: 348px;
  flex-direction: column;
  align-items: center;
  gap: 21px;
`;

const SignUpText = styled.div`
  color: #332115;
  text-align: center;
  font-family: "Plus Jakarta Sans";
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.96px;

  @media (max-width: 1023.98px) {
    font-size: 22px;
  }
`;

const SignUpInput = styled.input`
  display: flex;
  padding: 14px 12px;
  align-items: center;
  border-radius: 8px;
  background: #E3E2E2;
  width: 324px;
  border: none;
`;

const SignUpSectionButton = styled.button`
  display: flex;
  width: 268px;
  height: 65px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #205400;
  color: #FFF;
  font-family: "Plus Jakarta Sans";
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; 
  cursor: pointer;
  border: none;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;
`;

const Copyright = styled.div`
  color: #332115;
  text-align: center;
  font-family: "Plus Jakarta Sans";
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.32px;
`;

const Socials = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;

  > a > svg {
    height: 24px;
    width: auto;
    cursor: pointer;
  }
`;


const SignUpSection = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSignUp = async () => {
    console.log('clicked!')
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/get-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'An error occurred.');
        setLoading(false);
        return;
      }
      // could redirect to OTP for login OR signup page
      navigate(data.redirect_url, { state: data.state });
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      setLoading(false);
    }
  };

  return (
    <SignUpSectionWrapper>
      <SignUpSectionTitle>call.chat can make that call</SignUpSectionTitle>
      <SignUpContent>
        <SignUpText>First 30 days free!</SignUpText>
        <SignUpInput
          type="tel"
          placeholder="Enter your phone number"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          disabled={loading}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <SignUpSectionButton onClick={handleSignUp} disabled={loading || !phoneNumber}>
          Get Started
        </SignUpSectionButton>
      </SignUpContent>
      <Footer>
        <Copyright>© 2025 call.chat</Copyright>
        <Socials>
          <a href="https://www.instagram.com/call.chat.ai" target="_blank" rel="noopener noreferrer"><Instagram/></a>
          <a href="https://www.tiktok.com/@call.chat" target="_blank" rel="noopener noreferrer"><TikTok/></a>
        </Socials>
      </Footer>
    </SignUpSectionWrapper>
  );
}

const TopControlBarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  background: #fff;
  position: fixed;
  top: ${TICKER_BANNER_HEIGHT + 25}px;
  z-index: 9999;
  background: none;

  @media (min-width: 1024px) {
    left: ${SIDEBAR_WIDTH};
    right: 0;
  }

  @media (max-width: 1023.98px) {
    left: 0;
    right: 0;
  }
`;


const TopControlBar = styled.div`
  pointer-events: auto;
  display: flex;
  padding: 16px 24px;
  align-items: center;
  gap: 24px;
  justify-content: space-between;
  border-radius: 9999px;
  background: #fff;
  z-index: 9999;

  box-shadow:
    0 1px 1px rgba(0, 0, 0, 0.05),
    0 2px 2px rgba(0, 0, 0, 0.03);
`;


const ControlNavButton = styled.button`
  color: ${(props) => props.active ? "#332115" : "#756F6C"};
  font-family: "Plus Jakarta Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.28px;
  background: none;
  border: none;
  cursor: pointer;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: #FD8C0B;
    }
  }

  @media (hover: none) and (pointer: coarse) {
    &:active {
      color: #FD8C0B;
    }
  }    
`;

const TopControls = () => {
  const navigate = useNavigate();
  return (
    <TopControlBarWrapper>
      <TopControlBar>
        <ControlNavButton active onClick={() => navigate("/")}>About</ControlNavButton>
        <ControlNavButton onClick={() => navigate("/get-started")}>Sign Up</ControlNavButton>
        <ControlNavButton onClick={() => navigate("/manage-account")}>Log In</ControlNavButton>
      </TopControlBar>
    </TopControlBarWrapper>
  );
}


const Layout = styled.div`
  width: 100%;
  min-height: 100dvh;
  @media (min-width: ${MOBILE_WIDTH}) {
    display: block; /* sidebar is fixed, main just flows with margin-left */
  }
`;


const MainContent = styled.main`
  display: flex;
  flex-direction: column;

  @media (min-width: ${MOBILE_WIDTH}) {
    margin-left: ${SIDEBAR_WIDTH};
  }
`;

const ProductInfoSection = styled.section`
  position: relative;
  z-index: 3;
  background: #F9F1E2;
  padding: 80px 20px;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 100%;
`;

const LandingPage = () => {
  const navigate = useNavigate();

  const onSignUp = () => {
    navigate("/get-started");
  };

  return (
    <>
      <GlobalStyle />
      <Layout>
        <TickerSection/>
        <TopControls/>
        <SideBar onSignUp={onSignUp}/>
        <MainContent>
          <HeroSection/>
          <ProductInfoSection>
            <HowItWorksSection/>
            <UseCasesSection/>
            <FAQSection/>
          </ProductInfoSection>
          <SignUpSection/>
        </MainContent>
      </Layout>
    </>
  );
};

export default LandingPage;
